import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Sparkles,
  Package,
  Shield,
  Tag,
  Layers,
  Building2,
  Hash,
  Cpu,
  IndianRupee,
  Calendar,
  Store,
  Bell,
  FileText,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import { Equipment, typeMap, typeRevMap, statusMap, statusRevMap, STATUSES } from "./EquipmentConstants";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  ProgressWrap,
  ProgressLabel,
  ProgressPct,
  ProgressTrack,
  ProgressFill,
  ProgressHint,
  ScrollBody,
  FormGrid,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  FieldGrid,
  Field,
  FieldFull,
  Label,
  RequiredStar,
  Input,
  Select,
  SubmitBar,
  SubmitHint,
  SubmitButton,
  gradient,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";
import {
  Category,
  createEquipmentItem,
  updateEquipmentItem,
  getEquipmentItemDetail,
  getCategories,
} from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import { useAppSelector } from "../../../../Redux/Hooks";

function getEquipmentProgress(fields: {
  name: string;
  category: string;
  brand: string;
  quantity: string;
  price: string;
}) {
  let done = 0;
  const total = 5;
  if (fields.name.trim().length >= 2) done += 1;
  if (fields.category) done += 1;
  if (fields.brand.trim().length >= 1) done += 1;
  if (Number(fields.quantity) >= 1) done += 1;
  if (fields.price && Number(fields.price) > 0) done += 1;
  return Math.round((done / total) * 100);
}

const SerialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SerialRow = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 10px;
  align-items: center;
`;

const SerialIndex = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-align: center;
`;

const HintText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
  line-height: 1.4;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  padding: 14px 16px;
  color: #171717;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(23, 23, 23, 0.08);
  margin: 0;
`;

const ReminderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReminderTitle = styled.h4`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    appearance: none;
    border: 1px solid rgba(23, 23, 23, 0.22);
    border-radius: 5px;
    background: #fafafa;
    display: grid;
    place-content: center;
    flex-shrink: 0;
    transition: all 0.12s ease;
  }
  input::before {
    content: "";
    width: 9px;
    height: 9px;
    transform: scale(0);
    transition: transform 0.12s ease;
    clip-path: polygon(14% 44%, 0 59%, 37% 100%, 100% 18%, 84% 5%, 36% 70%);
    background: #ffffff;
  }
  input:checked {
    border-color: transparent;
    background: ${gradient};
  }
  input:checked::before {
    transform: scale(1);
  }
`;

const EquipmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { branches, selectedBranch } = useAppSelector((state) => state.auth);

  const [selectedBranchId, setSelectedBranchId] = useState<string | number>(selectedBranch?.id || "");

  const prevSelectedBranchIdRef = useRef<number | null | undefined>(selectedBranch?.id);
  const isEquipmentLoadedRef = useRef(false);

  useEffect(() => {
    if (prevSelectedBranchIdRef.current !== selectedBranch?.id) {
      prevSelectedBranchIdRef.current = selectedBranch?.id;
      if (!isEdit || isEquipmentLoadedRef.current) {
        setSelectedBranchId(selectedBranch?.id ? String(selectedBranch.id) : "");
      }
    }
  }, [selectedBranch?.id, isEdit]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [categoriesList, setCategoriesList] = useState<Category[]>([]);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [serialNumbers, setSerialNumbers] = useState<string[]>([""]);
  const [type, setType] = useState<Equipment["type"]>("Studio Owned");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState<Equipment["status"]>("Available");

  const [purchaseDate, setPurchaseDate] = useState("");
  const [purchaseVendor, setPurchaseVendor] = useState("");
  const [warrantyStart, setWarrantyStart] = useState("");
  const [warrantyExpiry, setWarrantyExpiry] = useState("");
  const [nextServiceDueDate, setNextServiceDueDate] = useState("");
  const [serviceNotes, setServiceNotes] = useState("");

  const [warrantyReminder, setWarrantyReminder] = useState(true);
  const [serviceReminder, setServiceReminder] = useState(true);
  const [saving, setSaving] = useState(false);

  const progress = useMemo(
    () =>
      getEquipmentProgress({
        name,
        category,
        brand,
        quantity,
        price,
      }),
    [name, category, brand, quantity, price]
  );

  const syncSerialFields = (qty: number, existing?: string[]) => {
    const next = Array.from({ length: Math.max(1, qty) }, (_, i) => existing?.[i] ?? "");
    setSerialNumbers(next);
  };

  const handleQuantityChange = (value: string) => {
    setQuantity(value);
    const qty = Math.max(1, Number(value) || 1);
    syncSerialFields(qty, serialNumbers);
  };

  const handleSerialChange = (index: number, value: string) => {
    setSerialNumbers((prev) => prev.map((s, i) => (i === index ? value : s)));
  };

  const formReady = progress === 100;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategories({ active_only: true });
        const cats = res.results || [];
        setCategoriesList(cats);
        if (cats.length > 0 && !category) {
          setCategory(String(cats[0].id));
        }
      } catch (err: any) {
        showErrorToast(err?.message || "Failed to load categories");
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      const loadDetail = async () => {
        try {
          const item = await getEquipmentItemDetail(id);
          if (item.branch_id) {
            setSelectedBranchId(String(item.branch_id));
          } else {
            setSelectedBranchId("");
          }
          isEquipmentLoadedRef.current = true;
          prevSelectedBranchIdRef.current = selectedBranch?.id;
          setName(item.name);
          setCategory(String(item.category));
          setBrand(item.brand || "");
          setModel(item.model || "");
          const qty = item.total_quantity ?? item.quantity ?? 1;
          setQuantity(String(qty));
          const loadedSerials =
            item.items?.map((u) => u.serial_number || "") ||
            (item.serial_number ? [item.serial_number] : [""]);
          syncSerialFields(qty, loadedSerials);
          setType(typeMap[item.ownership_type] || "Studio Owned");
          setPrice(item.purchase_price || "");
          setStatus(statusMap[item.status] || "Available");
          setPurchaseDate(item.purchase_date || "");
          setPurchaseVendor(item.purchase_vendor || "");
          setWarrantyStart(item.warranty_start_date || "");
          setWarrantyExpiry(item.warranty_expiry_date || "");
          setNextServiceDueDate(item.next_service_date || "");
          setServiceNotes(item.service_notes || "");
        } catch {
          showErrorToast("Equipment not found");
          navigate("/dashboard/equipment");
        }
      };
      loadDetail();
    }
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showErrorToast("Equipment name is required");
      return;
    }
    if (!category) {
      showErrorToast("Category is required");
      return;
    }
    if (!brand.trim()) {
      showErrorToast("Brand is required");
      return;
    }
    const qty = Number(quantity);
    if (!qty || qty < 1) {
      showErrorToast("Quantity must be at least 1");
      return;
    }
    if (!price || Number(price) <= 0) {
      showErrorToast("Please specify a valid purchase price");
      return;
    }

    const items = Array.from({ length: qty }, (_, i) => {
      const sn = (serialNumbers[i] || "").trim();
      return sn ? { serial_number: sn } : {};
    });
    const firstSerial = items.find((it) => "serial_number" in it && it.serial_number)?.serial_number;

    const payload = {
      branch_id: selectedBranchId ? Number(selectedBranchId) : null,
      name,
      category: Number(category),
      brand,
      model,
      quantity: qty,
      items,
      serial_number: firstSerial || "",
      ownership_type: typeRevMap[type] || "studio_owned",
      purchase_price: price,
      status: statusRevMap[status] || "available",
      purchase_date: purchaseDate || null,
      purchase_vendor: purchaseVendor || "",
      warranty_start_date: warrantyStart || null,
      warranty_expiry_date: warrantyExpiry || null,
      next_service_date: nextServiceDueDate || null,
      service_notes: serviceNotes || "",
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateEquipmentItem(id, payload);
        showSuccessToast("Equipment updated successfully!");
      } else {
        await createEquipmentItem(payload);
        showSuccessToast("Equipment added successfully!");
      }
      navigate("/dashboard/equipment");
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to save equipment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <FormGradientDefs />
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton
              type="button"
              onClick={() => navigate("/dashboard/equipment")}
              aria-label="Back to equipment list"
            >
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={11} />
                  Equipment Inventory
                </HeroBadge>
                <PageTitle className="linear-text">
                  {isEdit ? "Edit Equipment Details" : "Register New Equipment"}
                </PageTitle>
                <PageSubtitle>
                  {isEdit
                    ? "Update equipment details, warranty dates, and service reminders."
                    : "Add a new item to your studio inventory with purchase and warranty info."}
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          <ProgressWrap>
            <ProgressLabel>
              Form completion
              <ProgressPct>{progress}%</ProgressPct>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill $pct={progress} />
            </ProgressTrack>
            <ProgressHint>
              {formReady ? "Ready to save" : "Fill required fields to continue"}
            </ProgressHint>
          </ProgressWrap>
        </HeroRow>
      </Hero>

      <ScrollBody>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormCard>
              <CardHeader>
                <CardIcon $tone="#c94daa">
                  <Package size={20} />
                </CardIcon>
                <div>
                  <CardTitle>General Information</CardTitle>
                  <CardSubtitle>Basic details and inventory classification</CardSubtitle>
                </div>
              </CardHeader>

              {branches && branches.length > 0 && (
                <FieldFull>
                  <Label htmlFor="eq-branch">
                    <FormLabelIcon icon={Building2} />
                    Branch (Optional)
                  </Label>
                  <Select
                    id="eq-branch"
                    value={selectedBranchId}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                  >
                    <option value="">All Branches / Main Studio (HQ)</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branch_name} {b.city ? `(${b.city})` : ''}
                      </option>
                    ))}
                  </Select>
                </FieldFull>
              )}

              <FieldFull>
                <Label htmlFor="eq-name">
                  <FormLabelIcon icon={Tag} />
                  Equipment Name
                  <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  id="eq-name"
                  type="text"
                  placeholder="e.g. Sony Alpha 7 IV"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </FieldFull>

              <FieldGrid>
                <Field>
                  <Label htmlFor="eq-category">
                    <FormLabelIcon icon={Layers} />
                    Category
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <Select
                    id="eq-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categoriesList.length === 0 && (
                      <option value="">Loading categories...</option>
                    )}
                    {categoriesList.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label htmlFor="eq-brand">
                    <FormLabelIcon icon={Building2} />
                    Brand
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input
                    id="eq-brand"
                    type="text"
                    placeholder="e.g. Sony"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    required
                  />
                </Field>
              </FieldGrid>

              <FieldGrid>
                <Field>
                  <Label htmlFor="eq-model">
                    <FormLabelIcon icon={Cpu} />
                    Model Name
                  </Label>
                  <Input
                    id="eq-model"
                    type="text"
                    placeholder="e.g. ILCE-7M4"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label htmlFor="eq-quantity">
                    <FormLabelIcon icon={Layers} />
                    Quantity
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input
                    id="eq-quantity"
                    type="number"
                    min={1}
                    placeholder="e.g. 5"
                    value={quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    required
                  />
                </Field>
              </FieldGrid>

              <FieldFull>
                <Label>
                  <FormLabelIcon icon={Hash} />
                  Serial Numbers
                </Label>
                <HintText style={{ marginBottom: 10 }}>
                  Optional — leave blank for units without serials. One field per unit ({Math.max(1, Number(quantity) || 1)}).
                </HintText>
                <SerialList>
                  {serialNumbers.map((sn, idx) => (
                    <SerialRow key={idx}>
                      <SerialIndex>Unit {idx + 1}</SerialIndex>
                      <Input
                        type="text"
                        placeholder={`e.g. CAM-00${idx + 1}`}
                        value={sn}
                        onChange={(e) => handleSerialChange(idx, e.target.value)}
                      />
                    </SerialRow>
                  ))}
                </SerialList>
              </FieldFull>

              <FieldGrid>
                <Field>
                  <Label htmlFor="eq-type">
                    <FormLabelIcon icon={Package} />
                    Equipment Type
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <Select
                    id="eq-type"
                    value={type}
                    onChange={(e) => setType(e.target.value as Equipment["type"])}
                  >
                    <option value="Studio Owned">Studio Owned</option>
                    <option value="Available for Rent">Available for Rent</option>
                    <option value="Available for Sale">Available for Sale</option>
                  </Select>
                </Field>
                <Field>
                  <Label htmlFor="eq-price">
                    <FormLabelIcon icon={IndianRupee} />
                    Purchase Price (₹)
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <Input
                    id="eq-price"
                    type="number"
                    placeholder="e.g. 2499"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                  />
                </Field>
              </FieldGrid>

              {isEdit && (
                <Field>
                  <Label htmlFor="eq-status">
                    <FormLabelIcon icon={Tag} />
                    Inventory Status
                  </Label>
                  <Select
                    id="eq-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as Equipment["status"])}
                  >
                    {STATUSES.map((stat) => (
                      <option key={stat} value={stat}>
                        {stat}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </FormCard>

            <FormCard>
              <CardHeader>
                <CardIcon $tone="#35b7c8">
                  <Shield size={20} />
                </CardIcon>
                <div>
                  <CardTitle>Purchase & Warranty Lifecycle</CardTitle>
                  <CardSubtitle>Dates, vendor info, and maintenance notes</CardSubtitle>
                </div>
              </CardHeader>

              <FieldGrid>
                <Field>
                  <Label htmlFor="eq-purchase-date">
                    <FormLabelIcon icon={Calendar} />
                    Purchase Date
                  </Label>
                  <BrandDatePicker
                    name="eq-purchase-date"
                    value={purchaseDate}
                    onChange={(v) => setPurchaseDate(v)}
                  />
                </Field>
                <Field>
                  <Label htmlFor="eq-vendor">
                    <FormLabelIcon icon={Store} />
                    Purchase Vendor
                  </Label>
                  <Input
                    id="eq-vendor"
                    type="text"
                    placeholder="e.g. B&H Photo Video"
                    value={purchaseVendor}
                    onChange={(e) => setPurchaseVendor(e.target.value)}
                  />
                </Field>
              </FieldGrid>

              <FieldGrid>
                <Field>
                  <Label htmlFor="eq-warranty-start">
                    <FormLabelIcon icon={Calendar} />
                    Warranty Start Date
                  </Label>
                  <BrandDatePicker
                    name="eq-warranty-start"
                    value={warrantyStart}
                    onChange={(v) => setWarrantyStart(v)}
                  />
                </Field>
                <Field>
                  <Label htmlFor="eq-warranty-expiry">
                    <FormLabelIcon icon={Calendar} />
                    Warranty Expiry Date
                  </Label>
                  <BrandDatePicker
                    name="eq-warranty-expiry"
                    value={warrantyExpiry}
                    onChange={(v) => setWarrantyExpiry(v)}
                  />
                </Field>
              </FieldGrid>

              <Field>
                <Label htmlFor="eq-service-due">
                  <FormLabelIcon icon={Calendar} />
                  Next Scheduled Service Due
                </Label>
                <BrandDatePicker
                  name="eq-service-due"
                  value={nextServiceDueDate}
                  onChange={(v) => setNextServiceDueDate(v)}
                />
              </Field>

              <Field>
                <Label htmlFor="eq-service-notes">
                  <FormLabelIcon icon={FileText} />
                  Servicing & Maintenance Notes
                </Label>
                <TextArea
                  id="eq-service-notes"
                  placeholder="Describe current condition, history of issues or recent maintenance details..."
                  value={serviceNotes}
                  onChange={(e) => setServiceNotes(e.target.value)}
                />
              </Field>

              <Divider />

              <ReminderSection>
                <ReminderTitle>
                  <Bell size={13} />
                  Notifications & Alerts
                </ReminderTitle>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={warrantyReminder}
                    onChange={(e) => setWarrantyReminder(e.target.checked)}
                  />
                  Email alert 30 days before warranty expires
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={serviceReminder}
                    onChange={(e) => setServiceReminder(e.target.checked)}
                  />
                  Email alert when next service date is due
                </CheckboxLabel>
              </ReminderSection>
            </FormCard>
          </FormGrid>

          <SubmitBar>
            <SubmitHint>
              {isEdit
                ? "Review changes before updating this equipment record."
                : "All required fields must be completed before registering equipment."}
            </SubmitHint>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <SubmitButton
                type="button"
                $ready
                style={{
                  background: "#fff",
                  color: "rgba(23,23,23,0.72)",
                  border: "1px solid rgba(23,23,23,0.12)",
                  boxShadow: "none",
                  minWidth: 120,
                }}
                onClick={() => navigate("/dashboard/equipment")}
              >
                Cancel
              </SubmitButton>
              <SubmitButton type="submit" $ready={formReady && !saving} disabled={!formReady || saving}>
                <Save size={16} />
                {saving
                  ? "Saving…"
                  : isEdit
                    ? "Update Details"
                    : "Register Equipment"}
              </SubmitButton>
            </div>
          </SubmitBar>
        </form>
      </ScrollBody>
    </PageContainer>
  );
};

export default EquipmentFormPage;
